import { useCallback, useRef, useState, useEffect } from "react";

export interface UsePauseableRecorderReturn {
  status: "inactive" | "recording" | "paused";
  start: (stream: MediaStream, priorDurationSec?: number) => void;
  pause: () => void;
  resume: () => void;
  stop: () => Promise<Blob>;
  activeDuration: number;
}

/**
 * Detect the best supported recording MIME type.
 * Safari supports video/mp4; Chrome/Firefox prefer video/webm.
 */
function getPreferredMimeType(): string {
  const candidates = [
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "";
}

export function usePauseableRecorder(videoBitsPerSecond?: number): UsePauseableRecorderReturn {
  const [status, setStatus] = useState<"inactive" | "recording" | "paused">(
    "inactive",
  );
  const [activeDuration, setActiveDuration] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const resolveStopRef = useRef<((blob: Blob) => void) | null>(null);

  // Active-time tracking: array of { start, end? } segments
  const segmentsRef = useRef<Array<{ start: number; end?: number }>>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // VIZ-343: Offset from previous takes so the timer shows cumulative session time
  const priorMsRef = useRef(0);

  /** Compute total active milliseconds from segments + prior takes. */
  const computeActiveMs = useCallback(() => {
    let total = priorMsRef.current;
    for (const seg of segmentsRef.current) {
      const end = seg.end ?? Date.now();
      total += end - seg.start;
    }
    return total;
  }, []);

  /** Start the tick timer that updates activeDuration every second. */
  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      setActiveDuration(Math.floor(computeActiveMs() / 1000));
    }, 500);
  }, [computeActiveMs]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    };
  }, [stopTimer]);

  const start = useCallback(
    (stream: MediaStream, priorDurationSec = 0) => {
      chunksRef.current = [];
      segmentsRef.current = [];
      // VIZ-343: Carry forward cumulative time from previous takes
      priorMsRef.current = priorDurationSec * 1000;
      setActiveDuration(priorDurationSec);

      const mimeType = getPreferredMimeType();
      const options: MediaRecorderOptions = { mimeType };
      if (videoBitsPerSecond && videoBitsPerSecond > 0) {
        options.videoBitsPerSecond = videoBitsPerSecond;
      }
      // Use a reasonable timeslice so we get periodic data
      const recorder = new MediaRecorder(stream, options);
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        // Close the last open segment
        const segs = segmentsRef.current;
        if (segs.length > 0 && !segs[segs.length - 1].end) {
          segs[segs.length - 1].end = Date.now();
        }
        setActiveDuration(Math.floor(computeActiveMs() / 1000));
        stopTimer();

        const finalBlob = new Blob(chunksRef.current, {
          type: mimeType || "video/webm",
        });
        if (resolveStopRef.current) {
          resolveStopRef.current(finalBlob);
          resolveStopRef.current = null;
        }
        setStatus("inactive");
      };

      recorder.start(1000);
      segmentsRef.current.push({ start: Date.now() });
      setStatus("recording");
      startTimer();
    },
    [computeActiveMs, startTimer, stopTimer],
  );

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    recorder.pause();

    // Close current segment
    const segs = segmentsRef.current;
    if (segs.length > 0 && !segs[segs.length - 1].end) {
      segs[segs.length - 1].end = Date.now();
    }
    setActiveDuration(Math.floor(computeActiveMs() / 1000));
    stopTimer();
    setStatus("paused");
  }, [computeActiveMs, stopTimer]);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;

    recorder.resume();
    segmentsRef.current.push({ start: Date.now() });
    setStatus("recording");
    startTimer();
  }, [startTimer]);

  const stop = useCallback((): Promise<Blob> => {
    return new Promise<Blob>((resolve, reject) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        reject(new Error("Recorder is not active"));
        return;
      }
      resolveStopRef.current = resolve;
      recorder.stop();
    });
  }, []);

  return { status, start, pause, resume, stop, activeDuration };
}
